<script lang="ts">
export default {
  name: 'CompaniesFoodMenusUploadedFiles',
}
</script>
<script setup lang="ts">
import { computed, ref, onMounted } from 'vue'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { KkBox, KkButton, KkLoading } from '../../../components/base'
import { KkPagination, KkInfo } from '../../../components/ui'
import { KkUploadedCsvTable, KkCsvFileErrorModal } from '../../../domains/foodMenu/'
import type { ReadFoodMenuCsvFilesApiParams, ImportError, FoodMenuCsvFile } from '../../../domains/foodMenu/stores'

import { useFoodMenuCsvFileStore } from '../../../domains/foodMenu/stores'
import { usePagination } from 'src/biz_spa/composables/usePagination'

const route = useRoute()
const { t } = useI18n()

defineEmits(['update-title', 'update-breadcrumbs', 'update-progress'])

/** Pinia Store */
const foodMenuCsvFileStore = useFoodMenuCsvFileStore()
const { foodMenuCsvFiles, foodMenuCsvFilesMeta, isLoading } = storeToRefs(foodMenuCsvFileStore)
const { readFoodMenuCsvFiles, downloadTemplate, uploadFile, downloadFile, deleteFile } = foodMenuCsvFileStore

const {
  currentPage,
  displayNumber,
  sortOrder,
  setCurrentPage,
  setDisplayNumber,
  setSortOrder,
} = usePagination()

/** Ref */
const importErrors = ref<ImportError[]>([])
const showErrorModal = ref<boolean>(false)

/** Computed */
const companyUuid = computed<string>(() => route.params.company_uuid as string)

/** Methods */
const handleSort = ({ sortOrder }: {  sortOrder: 'desc' | 'asc' }): void => {
  if (isLoading.value) return
  setSortOrder(sortOrder)
  setCurrentPage(1)
  handleScrollTopAndRead()
}

/** pagination related */
const goPrevPage = (): void => {
  setCurrentPage(currentPage.value - 1)
  handleScrollTopAndRead()
}
const goNextPage = (): void => {
  setCurrentPage(currentPage.value + 1)
  handleScrollTopAndRead()
}

const changeDisplayNumber = (event: Event): void => {
  setDisplayNumber(Number((event.target as HTMLSelectElement).value))
  handleScrollTopAndRead()
}

/** file related */
const clickDownloadTemplate = ():void => {
  downloadTemplate()
}

const clickDownloadFile = (item: FoodMenuCsvFile): void => {
  downloadFile({ id: item.id, filename: item.originalFilename })
}

const clickDeleteFile = (item: FoodMenuCsvFile): void => {
  if (item.importStatus === 'error' || confirm(t('pages.CompaniesFoodMenus.uploadedFiles.deleteFileNote'))) {
    deleteFile({ id: item.id })
  }
}

const selectFile = async(e: Event): Promise<void> => {
  const input = e.target as HTMLInputElement
  if (!input || !input.files || input.files.length === 0) return

  const data = new FormData()
  data.append('food_menu_file[file]', input.files[0])
  const isSuccess = await uploadFile({data: data})
  if(isSuccess) readFiles()
}

/** modal related */
const handleDisplayErrorModal = (errors: ImportError[]): void => {
  importErrors.value = errors
  showErrorModal.value = true
}

const closeErrorModal = (): void => {
  showErrorModal.value = false
}

const readFiles = async (): Promise<void> => {
  try {
    const params: ReadFoodMenuCsvFilesApiParams = {
      per: displayNumber.value,
      page: currentPage.value,
      sort_order: sortOrder.value ? sortOrder.value : 'desc',
    }
    await readFoodMenuCsvFiles(params)
  } catch (error) {
    console.error('error:', error)
  }
}

const handleScrollTopAndRead = (): void =>{
  window.scrollTo({ top: 0 })
  readFiles()
}

const load = async (): Promise<void> => {
  await readFiles()
}
onMounted(load)
</script>

<template>
  <KkBox>
    <template #header>
      <div class="buttonArea">
        <KkButton
          height="32px"
          :font-bold="false"
          color="secondary"
          :text="t('pages.CompaniesFoodMenus.uploadedFiles.downloadTemplate')"
          @click="clickDownloadTemplate"
        />
        <label for="fileUpload" class="c-button__item c-button__item--secondary csvUploadButton">
          {{ t('pages.CompaniesFoodMenus.uploadedFiles.uploadCsvFile') }}
          <input id="fileUpload" type="file" name="file" accept=".csv, text/csv" @change="selectFile">
        </label>
      </div>
    </template>
    <template #content>
      <KkInfo icon="hint">
        <i18n-t keypath="pages.CompaniesFoodMenus.uploadedFiles.info" tag="div">
          <template #indexLink>
            <router-link
              :to="{ name: 'CompaniesFoodMenusIndex', params: { company_uuid: companyUuid} }"
            >
              {{ $t(`pages.CompaniesFoodMenus.uploadedFiles.foodMenuIndex`) }}
            </router-link>
          </template>
        </i18n-t>
      </KkInfo>
      <KkLoading v-if="isLoading" />
      <div v-else>
        <div v-if="foodMenuCsvFiles">
          <div class="totalCount">
            {{ $t('pages.CompaniesFoodMenus.uploadedFiles.totalCount', { totalCount: foodMenuCsvFilesMeta.totalCount }) }}
          </div>
          <KkUploadedCsvTable
            :food-menu-csv-files="foodMenuCsvFiles"
            :sort-order="sortOrder ? sortOrder : 'desc'"
            @delete-file="clickDeleteFile"
            @download-file="clickDownloadFile"
            @click-errors="handleDisplayErrorModal"
            @sort="handleSort"
          />
          <KkPagination
            :loading="isLoading"
            :current-page="currentPage"
            :display-number="Number(displayNumber)"
            :contents-length="foodMenuCsvFiles.length"
            :total-pages="foodMenuCsvFilesMeta.totalPages"
            @onChangeDisplayNumber="changeDisplayNumber($event)"
            @goPrevPage="goPrevPage"
            @goNextPage="goNextPage"
          />
        </div>
      </div>
      <KkCsvFileErrorModal
        :show-modal="showErrorModal"
        :errors="importErrors"
        @close-modal="closeErrorModal"
      />
    </template>
  </KkBox>
</template>

<style lang="scss" scoped>
@import "../../../../../stylesheets/biz_spa/foundation/_var.scss";
@import "../../../../../stylesheets/biz_spa/foundation/_mixin.scss";
.buttonArea {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  gap: 10px;
  @include mq(xs) {
    justify-content: flex-end;
  }
}
.totalCount {
  margin: 20px 0;
  font-weight: bold;
  font-size: 12px;
}
.csvUploadButton {
  max-width: 160px;
  height: 32px;
  margin-left: 0;
  input {
    display: none;
  }
  @include mq(xs) {
    margin-left: 8px;
  }
}
</style>
